import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useQuery, useQueryClient } from 'react-query';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { formatCurrency } from '../../utils/currency';
import { PencilIcon, EyeIcon, PlusIcon, TrashIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { lienAchat, dateCourte } from '../../utils/achats';

const MyProjects = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const handleDelete = async (project) => {
    if (!window.confirm(`Supprimer définitivement le projet « ${project.title} » ? Cette action est irréversible.`)) return;
    try {
      await api.delete(`/projects/${project._id || project.id}`);
      toast.success('Projet supprimé');
      queryClient.invalidateQueries('my-projects');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erreur lors de la suppression du projet');
    }
  };

  // Récupérer les projets de l'utilisateur
  const { data: projects = [], isLoading } = useQuery(
    'my-projects',
    async () => {
      const response = await api.get('/projects/my-projects');
      console.log('🔍 [MyProjects] response.data:', response.data);

      // Le backend retourne { success: true, data: [...] }
      // L'intercepteur api.js auto-extrait data quand success: true
      let myProjects = [];
      if (Array.isArray(response.data)) {
        myProjects = response.data;
      } else if (Array.isArray(response.data?.data)) {
        myProjects = response.data.data;
      } else {
        console.error('❌ Réponse backend invalide:', response.data);
        myProjects = [];
      }

      console.log('📋 Mes projets:', myProjects.length);
      return myProjects;
    },
    {
      enabled: !!user,
      onError: (error) => {
        console.error('Erreur chargement projets:', error);
        toast.error('Erreur lors du chargement de vos projets');
      }
    }
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };


  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement de vos projets...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-text mb-2">
              Mes Projets
            </h1>
            <p className="text-gray-600">
              Gérez et suivez vos projets soumis
            </p>
          </div>
          
          <Link
            to="/projects/create"
            className="inline-flex items-center px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Nouveau Projet
          </Link>
        </div>

        {/* Liste des projets */}
        {projects.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-text mb-2">
              Aucun projet soumis
            </h3>
            <p className="text-gray-600 mb-6">
              Vous n'avez pas encore soumis de projet. Commencez par créer votre premier projet !
            </p>
            <Link
              to="/projects/create"
              className="inline-flex items-center px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Créer mon premier projet
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <div key={project._id || project.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-text mb-2 line-clamp-2">
                        {project.title}
                      </h3>
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="px-2 py-1 bg-primary-100 text-primary-800 text-xs rounded-full">
                          {project.category}
                        </span>
                        <span className="px-2 py-1 bg-secondary-100 text-secondary-800 text-xs rounded-full">
                          {project.stage}
                        </span>
                      </div>
                    </div>
                  </div>

                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {project.description}
                  </p>

                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Financement:</span>
                      <span className="font-semibold text-text">
                        {formatCurrency(project.funding?.amountNeeded, project.funding?.currency)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Pays:</span>
                      <span className="font-medium text-text">{project.location?.country || project.countryName || project.country}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Créé le:</span>
                      <span className="font-medium text-text">{formatDate(project.createdAt)}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex space-x-2">
                    <Link
                      to={`/projects/${project._id || project.id}`}
                      className="flex-1 flex items-center justify-center px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      <EyeIcon className="h-4 w-4 mr-1" />
                      Voir
                    </Link>
                    <Link
                      to={`/projects/${project.id}/edit`}
                      className="flex-1 flex items-center justify-center px-3 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                    >
                      <PencilIcon className="h-4 w-4 mr-1" />
                      Modifier
                    </Link>
                    <button
                      type="button"
                      onClick={() => handleDelete(project)}
                      title="Supprimer le projet"
                      aria-label="Supprimer le projet"
                      className="flex items-center justify-center px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Mise en avant (achat à la semaine) : projets publiés seulement */}
                  {project.status === 'active' && (
                    project.featuredUntil ? (
                      <p className="mt-3 flex items-center text-sm text-amber-700">
                        <SparklesIcon className="h-4 w-4 mr-1" aria-hidden="true" />
                        Mis en avant jusqu'au {dateCourte(project.featuredUntil)}
                        <a href={lienAchat('avant_projet', project.id)} className="ml-2 underline">Prolonger</a>
                      </p>
                    ) : (
                      <a href={lienAchat('avant_projet', project.id)}
                        className="mt-3 flex items-center justify-center px-3 py-2 border border-amber-300 text-amber-800 rounded-lg hover:bg-amber-50 text-sm">
                        <SparklesIcon className="h-4 w-4 mr-1" aria-hidden="true" />
                        Mettre en avant pendant 7 jours
                      </a>
                    )
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Statistiques */}
        {projects.length > 0 && (
          <div className="mt-12 bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-semibold text-text mb-4">Statistiques</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-primary-500 mb-2">
                  {projects.length}
                </div>
                <div className="text-gray-600">Projets soumis</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-secondary-500 mb-2">
                  {projects.reduce((sum, p) => sum + (p.views || 0), 0)}
                </div>
                <div className="text-gray-600">Vues totales</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-500 mb-2">
                  {projects.reduce((sum, p) => sum + (p.likeCount || 0), 0)}
                </div>
                <div className="text-gray-600">Likes total</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyProjects;








